import React from 'react';
import styled from '@emotion/styled/macro';
import { Checkbox, Form, Input, Radio } from 'antd';
import FormList from 'antd/lib/form/FormList';

import {
  AddButtonWrapper,
  AddFormItemLabel,
  AddInput,
  AddModal,
  formLayout,
  formValidateMessages,
  FormWrapper,
  PrimaryButton,
  PrimaryButtonV2,
} from '../../../../utils';

import { UploadImageV2 } from '../../../../components';
import RichText from '../RichText';

const FormItem = Form.Item;

export default function RewardsModal(props) {
  const {
    showAddModal,
    handleCloseAddModal,
    isEvent,

    formRef,
    onFinish,
    setCanMove,
    showErrors,
    setShowErrors,

    image,
    setCoverImage,

    title,
    setTitle,

    price,
    setPrice,

    description,
    getContent,

    variants,
    setVariants,

    shippingDate,
    setShippingDate,

    addressRequired,
    setAddressRequired,

    noMultiOrder,
    setNoMultiOrder,

    showExtraDetails,
    setShowExtraDetails,

    extraDetails,
    setExtraDetails,

    showMaxStock,
    setShowMaxStock,

    maxOrderCount,
    setMaxOrderCount,
  } = props;

  return (
    <StyledAddModal
      showModal={showAddModal}
      noLogo
      handelCloseModal={handleCloseAddModal}
      width="640px"
      leftMargin={10}
      leftTitle={
        <TitleLabel>{isEvent ? 'Ticket' : 'Goodie'} hinzufügen</TitleLabel>
      }
    >
      <ModalContent>
        <Form
          ref={formRef}
          {...formLayout}
          name="rewards"
          id="reward-form"
          onChange={() => setCanMove?.(false)}
          onFinish={onFinish}
          validateMessages={formValidateMessages}
          onFinishFailed={() => setShowErrors?.(true)}
        >
          <StyledFormWrapper>
            <CoverImageBlock>
              <UploadImageV2
                multiple={false}
                label="Bild"
                name="image"
                width="200"
                height="200"
                recommendedSize="1000 x 1000"
                showErrors={showErrors}
                errorTitle="Bild"
                fileList={null}
                setImage={setCoverImage}
                images={image}
              />
            </CoverImageBlock>

            <ModalFormItem
              name="title"
              label={<AddFormItemLabel>Bezeichnung</AddFormItemLabel>}
              rules={[{ required: false, message: 'Titel ist erforderlich' }]}
              initialValue={title}
            >
              <AddInput
                placeholder="Titel"
                onChange={(e) => setTitle?.(e.target.value)}
              />
            </ModalFormItem>

            <ModalFormItem
              name="price"
              label={<AddFormItemLabel>Preis</AddFormItemLabel>}
              rules={[{ required: false, message: 'Preis ist erforderlich' }]}
              initialValue={price}
            >
              <CurrencyWrapper>
                <CurrencyAddon>EUR</CurrencyAddon>
                <AddInput
                  className="currency-input"
                  type="number"
                  step="any"
                  placeholder="Preis"
                  value={price}
                  onChange={(e) => {
                    const value = e.target.value;
                    const numericValue = Number(value);

                    setPrice?.(
                      value !== '' && !Number.isNaN(numericValue)
                        ? numericValue
                        : undefined
                    );
                  }}
                />
              </CurrencyWrapper>
            </ModalFormItem>

            <ModalFormItem
              name="description"
              label={<AddFormItemLabel>Beschreibung</AddFormItemLabel>}
              rules={[
                { required: false, message: 'Beschreibung ist erforderlich' },
              ]}
              initialValue={description}
            >
              <RichText
                name="description"
                style={{ fontSize: 16 }}
                getContent={getContent}
                placeholder="Beschreibung"
              />
            </ModalFormItem>

            <VariantsSection>
              <VariantHeader>
                <VariantTitle>Varianten</VariantTitle>
                <VariantDescription>
                  Falls dein Produkt verschiedene Auswahlmöglichkeiten bietet
                  (z. B. Größen, Farben), kannst du sie hier hinzufügen.
                </VariantDescription>
              </VariantHeader>

              <FormList name="variants" initialValue={variants || []}>
                {(fields, { add }) => (
                  <VariantList>
                    <AddVariantButton type="button" onClick={() => add()}>
                      Auswahlmöglichkeit hinzufügen
                    </AddVariantButton>

                    {fields.map(({ key, name, ...restField }) => (
                      <VariantGroup key={key}>
                        <VariantInputWrapper>
                          <FormItem
                            {...restField}
                            name={[name, 'attribute']}
                            style={{ marginBottom: 0, width: '100%' }}
                          >
                            <AddInput placeholder="Auswahlmöglichkeit z.B. Größe oder Farbe" />
                          </FormItem>
                        </VariantInputWrapper>

                        <FormList name={[name, 'options']}>
                          {(
                            optionFields,
                            { add: addOption, remove: removeOption }
                          ) => (
                            <VariantOptionsRow>
                              {optionFields.map(
                                ({
                                  key: optionKey,
                                  name: optionName,
                                  ...optionRest
                                }) => (
                                  <OptionBadge key={optionKey}>
                                    <FormItem
                                      {...optionRest}
                                      name={[optionName]}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <BadgeInput
                                        placeholder="Variante"
                                        onInput={(e) => {
                                          e.target.style.width = `calc(${Math.max(
                                            e.target.value.length,
                                            1
                                          )}ch + 16px)`;
                                        }}
                                      />
                                    </FormItem>

                                    <BadgeClose
                                      type="button"
                                      onClick={() => removeOption(optionName)}
                                    >
                                      ×
                                    </BadgeClose>
                                  </OptionBadge>
                                )
                              )}

                              <AddOptionButton
                                type="button"
                                onClick={() => addOption()}
                              >
                                Variante hinzufügen
                              </AddOptionButton>
                            </VariantOptionsRow>
                          )}
                        </FormList>

                        <FormItem noStyle shouldUpdate>
                          {() => {
                            const currentVariants =
                              formRef?.current?.getFieldValue('variants') || [];

                            if (
                              JSON.stringify(variants || []) !==
                              JSON.stringify(currentVariants)
                            ) {
                              setVariants?.(currentVariants);
                            }

                            return null;
                          }}
                        </FormItem>
                      </VariantGroup>
                    ))}
                  </VariantList>
                )}
              </FormList>
            </VariantsSection>

            {!isEvent && (
              <ModalFormItem
                name="shipping_date"
                label={<AddFormItemLabel>Lieferzeitpunkt</AddFormItemLabel>}
                rules={[
                  {
                    required: false,
                    message: 'Lieferzeitpunkt ist erforderlich',
                  },
                ]}
                initialValue={shippingDate ? 1 : 0}
              >
                <Radio.Group
                  onChange={(e) => setShippingDate?.(e.target.value)}
                  style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                >
                  <Radio value={1}>Goodie ist sofort verfügbar</Radio>
                  <Radio value={0}>
                    Goodie ist erst nach erfolgreichem Crowdfunding verfügbar
                  </Radio>
                </Radio.Group>
              </ModalFormItem>
            )}


              <SectionTitle>Weitere Angaben</SectionTitle>

              <StyledAddFormItem name="address_required">
                <Checkbox
                  checked={addressRequired}
                  onChange={(e) => setAddressRequired?.(e.target.checked)}
                >
                  Lieferadresse des Unterstützers benötigt
                </Checkbox>
              </StyledAddFormItem>

              <StyledAddFormItem name="no_multi_order">
                <Checkbox
                  checked={noMultiOrder}
                  onChange={(e) => setNoMultiOrder?.(e.target.checked)}
                >
                  {isEvent
                    ? 'Nur ein Ticket pro Unterstützer'
                    : 'Nur ein Goodie pro Unterstützer'}
                </Checkbox>
              </StyledAddFormItem>

              <StyledAddFormItem
                name="show_extra_details"
                extra={
                  isEvent
                    ? 'Falls dein Ticket eine Angabe vom Käufer benötigt, kannst du hier eine anfordern.'
                    : 'Falls dein Produkt personalisierbar ist, kannst du hier eine Angabe anfordern.'
                }
              >
                <Checkbox
                  checked={showExtraDetails}
                  onChange={(e) => setShowExtraDetails?.(e.target.checked)}
                >
                  Unterstützer müssen eine Angabe machen
                </Checkbox>
              </StyledAddFormItem>

              {showExtraDetails && (
                <StyledAddFormItem
                  name="extra_details"
                  initialValue={extraDetails}
                >
                  <AddInput
                    type="text"
                    min={1}
                    placeholder='Eingabeaufforderung (z. B. "Name für Gravur")'
                    onChange={(e) => setExtraDetails?.(e.target.value)}
                  />
                </StyledAddFormItem>
              )}

              <StyledAddFormItem name="show_max_stock">
                <Checkbox
                  checked={showMaxStock}
                  onChange={(e) => setShowMaxStock?.(e.target.checked)}
                >
                  Stückzahl ist limitiert
                </Checkbox>
              </StyledAddFormItem>

              {showMaxStock && (
                <StyledAddFormItem
                  name="max_order_count"
                  initialValue={maxOrderCount}
                >
                  <AddInput
                    type="number"
                    min={1}
                    placeholder="Stückzahl eingeben"
                    onChange={(e) => setMaxOrderCount?.(e.target.value)}
                  />
                </StyledAddFormItem>
              )}

            <Actions>
              <div className="actions-content">
                <PrimaryButton primary={true} onClick={handleCloseAddModal}>
                  Abbrechen
                </PrimaryButton>

                <PrimaryButton
                  primary={false}
                  htmlType="submit"
                  id="submit-button"
                >
                  Hinzufügen
                </PrimaryButton>
              </div>
            </Actions>
          </StyledFormWrapper>
        </Form>
      </ModalContent>
    </StyledAddModal>
  );
}

const modalShadow = `
  0px 20px 24px -4px rgba(10, 13, 18, 0.08),
  0px 8px 8px -4px rgba(10, 13, 18, 0.03),
  0px 3px 3px -1.5px rgba(10, 13, 18, 0.04)
`;

const inputShadow = `0px 1px 2px rgba(10, 13, 18, 0.05)`;

const skeuomorphicShadow = `
  0px 1px 2px rgba(10, 13, 18, 0.05),
  inset 0px 0px 0px 1px rgba(10, 13, 18, 0.18),
  inset 0px -2px 0px rgba(10, 13, 18, 0.05)
`;

const StyledAddModal = styled(AddModal)`
  .ant-modal {
    width: 640px !important;
    max-width: calc(100vw - 32px);
  }

  .ant-modal-content {
    width: 100%;
    max-width: 640px;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: ${modalShadow};
    overflow: hidden;
  }

  .ant-modal-body {
    padding: 0;
  }

  .ant-modal-close {
    width: 44px;
    height: 44px;
    top: 16px;
    right: 16px;
    border-radius: 8px;
    color: #a4a7ae;
  }
`;

const TitleLabel = styled.div`
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #181d27;
`;

const ModalContent = styled.div`
  width: 100%;

`;

const StyledFormWrapper = styled(FormWrapper)`
  width: 100%;
  max-width: 592px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;

  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-form-item-label {
    padding-bottom: 6px;
  }

  .ant-form-item-label > label {
    height: auto;
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: #414651;
  }

  .ant-form-item-explain-error {
    margin-top: 4px;
    font-size: 13px;
  }

  input,
  textarea,
  .ant-input {
    font-family: Inter, sans-serif;
  }

  .ant-checkbox-wrapper,
  .ant-radio-wrapper {
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #414651;
  }

  .ant-checkbox-inner {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    border-color: #d5d7da;
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: #9dbc60;
    border-color: #9dbc60;
  }

  .ant-checkbox-checked::after {
    border-color: #9dbc60;
  }

  .ant-radio-checked .ant-radio-inner {
    border-color: #9dbc60;
  }

  .ant-radio-inner::after {
    background-color: #9dbc60;
  }
`;

const CoverImageBlock = styled.div`
  width: 248px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  label,
  .label {
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: #414651;
  }

  button {
    min-height: 44px;
    border-radius: 8px;
    color: #3f621a;
    font-family: Inter, sans-serif;
    font-size: 16px;
    font-weight: 600;
  }
`;

const ModalFormItem = styled(FormItem)`
  width: 100%;
  max-width: 592px;

  input,
  textarea,
  .ant-input {
    width: 100%;
    min-height: 44px;
    padding: 10px 14px;
    background: #ffffff;
    border: 1px solid #d5d7da;
    border-radius: 8px;
    box-shadow: ${inputShadow};
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    color: #181d27;
  }

  textarea,
  .ql-container,
  .ql-editor {
    min-height: 118px;
  }
`;

const CurrencyWrapper = styled.div`
  width: 100%;
  height: 44px;
  display: flex;
  align-items: stretch;
  background: #ffffff;
  border: 1px solid #d5d7da;
  border-radius: 8px;
  box-shadow: ${inputShadow};
  overflow: hidden;

  .currency-input {
    flex: 1;
    min-width: 0;
    height: 44px;
    border: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }
`;

const CurrencyAddon = styled.div`
  flex: 0 0 58px;
  display: flex;
  align-items: center;
  padding: 10px 12px 10px 14px;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #535862;
  border-right: 1px solid #d5d7da;
`;

const VariantsSection = styled.div`
  width: 100%;
  max-width: 592px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const VariantHeader = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const VariantTitle = styled.div`
  width: 100%;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: #181d27;
`;

const VariantDescription = styled.div`
  width: 100%;
  max-width: 592px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #535862;
`;

const VariantList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const VariantGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const VariantInputWrapper = styled.div`
  width: 100%;

  input,
  .ant-input {
    width: 100%;
    height: 44px;
    padding: 10px 14px;
    background: #ffffff;
    border: 1px solid #d5d7da;
    border-radius: 8px;
    box-shadow: ${inputShadow};
    font-family: Inter, sans-serif;
    font-size: 16px;
    line-height: 24px;
    color: #181d27;
  }
`;

const VariantOptionsRow = styled.div`
  width: 100%;
  min-height: 28px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const OptionBadge = styled.div`
  min-height: 28px;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 4px 6px 4px 12px;
  background: #fafafa;
  border: 1px solid #e9eaeb;
  border-radius: 9999px;
  box-sizing: border-box;
`;

const BadgeInput = styled(Input)`
  width: 32px;
  min-width: 24px;
  max-width: 160px;
  height: 20px !important;
  min-height: 20px !important;
  padding: 0 !important;
  background: transparent !important;
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  font-family: Inter, sans-serif;
  font-size: 14px !important;
  font-weight: 500;
  line-height: 20px !important;
  text-align: center;
  color: #414651;

  &::placeholder {
    color: #717680;
    text-align: left;
  }

  &:focus {
    outline: none;
    box-shadow: none !important;
  }
`;

const BadgeClose = styled.button`
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 9999px;
  background: transparent;
  color: #a4a7ae;
  font-size: 16px;
  font-weight: 500;
  line-height: 16px;
  cursor: pointer;

  &:hover {
    color: #414651;
  }
`;

const AddVariantButton = styled.button`
  width: fit-content;
  min-height: 20px;
  padding: 0;
  border: 0;
  background: transparent;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #3f621a !important;
  cursor: pointer;
`;

const AddOptionButton = styled.button`
  width: fit-content;
  min-height: 20px;
  padding: 0;
  border: 0;
  background: transparent;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #3f621a;
  cursor: pointer;
`;

const SectionTitle = styled.div`
  width: 100%;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: #181d27;
`;

const StyledAddFormItem = styled(FormItem)`
  width: 100%;
  max-width: 592px;
  margin-bottom: 0;

  input,
  .ant-input {
    width: 100%;
    height: 44px;
    padding: 10px 14px;
    background: #ffffff;
    border: 1px solid #d5d7da;
    border-radius: 8px;
    box-shadow: ${inputShadow};
    font-family: Inter, sans-serif;
    font-size: 16px;
    line-height: 24px;
    color: #181d27;
  }

  .ant-form-item-extra {
    margin-top: 4px;
    font-family: Inter, sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: #535862;
  }
`;

const Actions = styled(AddButtonWrapper)`
  width: calc(100% + 48px);
  margin-left: -24px;
  padding: 32px 24px 0;

  .actions-content {
    width: 100%;
    display: flex;
    gap: 12px;
  }
  
  button {
    width: 100%
  
  
  ;
  }
`;
